import React, { memo, useCallback, useMemo, useRef } from 'react';
import GroceryItemCard from '../Cards/GroceryItemCard';
import Colors from '../../utils/colors/Colors';
import {
  formatCurrencyAmount,
  getOfferLineQuantity,
  isOfferEligible,
  mapSpecialPriceProductToCardItem,
  parseOfferMaxQty,
} from '../../utils/cartOfferUtils';

const MinPurchaseOfferSection = memo(({
  offerInfo,
  cartItems,
  colors,
  isLocationServicable,
  addToCart,
  removeFromCart,
  onCartChange,
  customerType,
  generalCustomerType,
}) => {
  const offer = offerInfo?.offer;
  const specialProducts = offer?.specialPriceProducts ?? [];
  const itemsTotal = cartItems?.billDetails?.itemsTotal;
  // Optimistic cart updates clear billDetails; keep last known total for eligibility.
  const lastItemsTotalRef = useRef(itemsTotal);
  if (itemsTotal != null && itemsTotal !== '') {
    lastItemsTotalRef.current = itemsTotal;
  }
  const resolvedItemsTotal = itemsTotal ?? lastItemsTotalRef.current;
  const amountNeeded = Number(offerInfo?.amountNeeded ?? 0);
  const isEligible = isOfferEligible(offerInfo, resolvedItemsTotal);
  const minPurchase = Number(offer?.minPurchaseAmount);
  const amountToUnlockCurrent =
    Number.isFinite(minPurchase) && resolvedItemsTotal != null
      ? Math.max(0, minPurchase - Number(resolvedItemsTotal))
      : amountNeeded;

  const offerCardItems = useMemo(
    () =>
      specialProducts.map((product) =>
        mapSpecialPriceProductToCardItem(product, offer, {
          customerType,
          generalCustomerType,
        }),
      ),
    [specialProducts, offer, customerType, generalCustomerType],
  );

  const handleOfferAddToCart = useCallback(
    async (item, index, locationOk, loginInfo, locationInfo) => {
      if (!isEligible || !locationOk || !offer?._id) {
        return;
      }
      if (!item?.productId || !item?.skuId) {
        return;
      }
      onCartChange?.();
      await addToCart(
        {
          ...item,
          itemId: item.productId,
          isOfferItem: true,
          offerId: offer._id,
          specialPrice: item.specialPrice,
          maxOfferQty: item.maxOfferQty,
          mrp: item.specialPrice,
          sellingPrice: item.specialPrice,
        },
        index,
        locationOk,
        loginInfo,
        locationInfo,
      );
    },
    [addToCart, isEligible, offer?._id, onCartChange],
  );

  const handleOfferRemoveFromCart = useCallback(
    async (item, index, locationOk, loginInfo, locationInfo) => {
      if (!offer?._id || !item?.productId) {
        return;
      }
      const line = cartItems?.items?.find(
        (cartItem) =>
          cartItem?.isOfferItem &&
          String(cartItem?.productId ?? cartItem?.itemId) === String(item.productId) &&
          String(cartItem?.offerId ?? '') === String(offer._id),
      );
      if (!line) {
        return;
      }
      onCartChange?.();
      await removeFromCart(
        {
          ...line,
          quantity: item?.quantity ?? line?.quantity,
        },
        index,
        locationOk,
        loginInfo,
        locationInfo,
      );
    },
    [cartItems?.items, offer?._id, onCartChange, removeFromCart],
  );

  if (!offerInfo || (!offer && !offerInfo?.message)) {
    return null;
  }

  return (
    <div
      className={`mb-3 p-3 rounded border border-appThemeColorAboveLight bg-${colors?.bottomTabBg} dark:bg-${colors?.bottomTabBg}`}
      style={{ backgroundColor: Colors.colorTheme[colors?.bottomTabBg] }}
    >
      <p className={`text-left text-base font-bold mb-1 text-${colors?.heading}`}>
        {offer?.name ?? 'Special Offer'}
      </p>

      {offerInfo?.message ? (
        <p className={`text-left text-xs mb-1 text-${colors?.heading}`}>
          {offerInfo.message}
        </p>
      ) : null}

      {!isEligible && amountToUnlockCurrent > 0 ? (
        <p className="text-left text-xs mb-2 text-appThemeColorVeryDark">
          {`Add ₹${formatCurrencyAmount(amountToUnlockCurrent)} more to unlock this offer`}
        </p>
      ) : null}

      {offerCardItems.length > 0 ? (
        <div className="flex overflow-x-auto gap-2 pb-1">
          {offerCardItems.map((item, index) => {
            const qtyInCart = getOfferLineQuantity(item?.productId, offer?._id, cartItems);
            const maxQty = parseOfferMaxQty(item?.maxOfferQty);
            const isMaxReached = maxQty != null && qtyInCart >= maxQty;
            const disableAdd =
              !isEligible ||
              !isLocationServicable ||
              !item?.productId ||
              !item?.skuId ||
              isMaxReached;

            return (
              <div
                key={`${offer?._id ?? 'offer'}-${item?.productId ?? 'product'}-${item?.skuId ?? index}`}
                className="flex-shrink-0"
              >
                <GroceryItemCard
                  item={item}
                  index={index}
                  cartItems={cartItems}
                  cartQuantity={qtyInCart}
                  customerType={customerType}
                  generalCustomerType={generalCustomerType}
                  isLocationServicable={isLocationServicable}
                  addToCart={handleOfferAddToCart}
                  removeFromCart={handleOfferRemoveFromCart}
                  disableAdd={disableAdd}
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});

export default MinPurchaseOfferSection;
